'use client';

import React from 'react';
import { MessageSquare, QrCode, Lock, ArrowUpRight, Check, Sparkles } from 'lucide-react';

interface WhatsAppHeroCardsProps {
  connected: boolean;
  phone?: string;
  onOpenQrModal: () => void;
  onDisconnect: () => void;
}

export function WhatsAppHeroCards({
  connected,
  phone = '+54 9 11 3176-7548',
  onOpenQrModal,
  onDisconnect
}: WhatsAppHeroCardsProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
      {/* Left Box: Vinculá tu WhatsApp */}
      <div className="lg:col-span-7 bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-7 shadow-sm flex flex-col justify-between space-y-6">
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100/80 flex items-center justify-center">
              <MessageSquare className="w-6 h-6" />
            </div>

            <span
              className={`text-[11px] font-bold px-3 py-1 rounded-full border flex items-center gap-1.5 ${
                connected
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                  : 'bg-amber-50 text-amber-800 border-amber-200'
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  connected ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'
                }`}
              />
              {connected ? `Vinculado (${phone})` : 'Sin vincular'}
            </span>
          </div>

          <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight mb-1">
              Vinculá tu WhatsApp
            </h2>
            <p className="text-xs text-slate-500 leading-relaxed">
              Escaneás un código con tu celular y tus mensajes empiezan a salir de tu propio número. No hace falta cambiar de línea ni instalar nada.
            </p>
          </div>

          {/* 3 Steps */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
            <div className="p-3 bg-slate-50 border border-slate-100 rounded-2xl space-y-1">
              <div className="w-5 h-5 rounded-full bg-emerald-600 text-white font-bold text-[10px] flex items-center justify-center">
                1
              </div>
              <h4 className="text-xs font-bold text-slate-900">Abrí WhatsApp</h4>
              <p className="text-[10px] text-slate-400">
                Ajustes → Dispositivos vinculados
              </p>
            </div>

            <div className="p-3 bg-slate-50 border border-slate-100 rounded-2xl space-y-1">
              <div className="w-5 h-5 rounded-full bg-emerald-600 text-white font-bold text-[10px] flex items-center justify-center">
                2
              </div>
              <h4 className="text-xs font-bold text-slate-900">Escaneá el código</h4>
              <p className="text-[10px] text-slate-400">
                Te lo mostramos acá, en pantalla
              </p>
            </div>

            <div className="p-3 bg-slate-50 border border-slate-100 rounded-2xl space-y-1">
              <div className="w-5 h-5 rounded-full bg-emerald-600 text-white font-bold text-[10px] flex items-center justify-center">
                3
              </div>
              <h4 className="text-xs font-bold text-slate-900">Listo</h4>
              <p className="text-[10px] text-slate-400">
                Los 5 mensajes se activan solos
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Action Button */}
        <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-3">
          {connected ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                id="btn-vincular-whatsapp"
                onClick={onOpenQrModal}
                className="px-5 py-3 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-xs rounded-2xl transition-all shadow-md shadow-emerald-700/20 flex items-center justify-center gap-2 cursor-pointer"
              >
                <QrCode className="w-4 h-4" />
                <span>Reconectar / Ver QR</span>
              </button>
              <button
                type="button"
                id="btn-desvincular-whatsapp"
                onClick={onDisconnect}
                className="px-5 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-2xl transition-all border border-slate-200 cursor-pointer"
              >
                Desvincular
              </button>
            </div>
          ) : (
            <button
              type="button"
              id="btn-vincular-whatsapp"
              onClick={onOpenQrModal}
              className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-xs rounded-2xl transition-all shadow-md shadow-emerald-700/20 flex items-center justify-center gap-2 cursor-pointer"
            >
              <QrCode className="w-4 h-4" />
              <span>Vincular ahora</span>
            </button>
          )}
          <span className="text-[11px] text-slate-400 font-medium text-center sm:text-left">
            Tarda menos de 2 minutos
          </span>
        </div>
      </div>

      {/* Right Box: Asistente WhatsApp IA (Próximamente en Calenddy) */}
      <div className="lg:col-span-5 bg-[#191923] text-white rounded-3xl p-6 sm:p-7 shadow-sm flex flex-col justify-between space-y-6 relative overflow-hidden">
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#6255F5]/30 border border-[#6255F5]/40 overflow-hidden flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-[#6255F5]" />
              </div>
              <div>
                <span className="text-[10px] font-bold text-[#6255F5] uppercase tracking-wider block">
                  AUTOMATIZACIÓN INTELIGENTE
                </span>
                <h3 className="text-base font-bold text-white">Asistente WhatsApp IA</h3>
              </div>
            </div>

            <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-white/10 text-white/90 border border-white/20 flex items-center gap-1">
              <Lock className="w-3 h-3" />
              <span>Próximamente</span>
            </span>
          </div>

          <p className="text-xs text-slate-300 leading-relaxed">
            Responde consultas frecuentes, coordina turnos y gestiona señas por WhatsApp las 24 horas con tu propio estilo.
          </p>

          <ul className="space-y-2 text-xs text-slate-300 pt-1">
            <li className="flex items-center gap-2">
              <Check className="w-3.5 h-3.5 text-[#6255F5] shrink-0 stroke-[3]" />
              <span>Contesta disponibilidad y agenda turnos sin tocar el celular</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-3.5 h-3.5 text-[#6255F5] shrink-0 stroke-[3]" />
              <span>Envía el link de pago de seña antes de registrar el turno</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-3.5 h-3.5 text-[#6255F5] shrink-0 stroke-[3]" />
              <span>Sincronización directa con tu agenda y profesionales</span>
            </li>
          </ul>
        </div>

        <div className="space-y-2 pt-2">
          <button
            type="button"
            className="w-full py-3 px-4 bg-[#6255F5] hover:bg-[#5144E5] text-white font-bold text-xs rounded-2xl transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer"
          >
            <span>Módulo en Desarrollo</span>
          </button>
          <p className="text-[10px] text-slate-400 text-center">
            Próximamente disponible para negocios con plan Calenddy Pro
          </p>
        </div>
      </div>
    </div>
  );
}
